import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/services/supabase.js';

const AuthContext = createContext(null);

// Check if a Supabase session exists in localStorage
// This is synchronous and fast — no network call
function getStoredUser() {
  try {
    const keys = Object.keys(localStorage);
    const key = keys.find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user || null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const storedUser = getStoredUser();

  const [user,      setUser]      = useState(storedUser);
  const [profile,   setProfile]   = useState(null);
  const [loading,   setLoading]   = useState(true); // Always start loading until we know auth state
  const [isCaptain, setIsCaptain] = useState(false);
  const fetchingRef = { current: false }; // Prevent duplicate fetches

  useEffect(() => {
    // If we had a stored user, fetch their profile immediately
    if (storedUser) {
      fetchingRef.current = true;
      fetchProfile(storedUser.id);
    }

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          if (session?.user) {
            setUser(session.user);
            // Fetch profile if not already fetching
            if (!fetchingRef.current) {
              fetchingRef.current = true;
              await fetchProfile(session.user.id);
            }
          } else {
            // INITIAL_SESSION with no session = definitely not logged in
            setUser(null);
            setProfile(null);
            setLoading(false);
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setIsCaptain(false);
          setLoading(false);
          fetchingRef.current = false;
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setUser(session.user);
        }
      }
    );

    // Safety timeout — if auth takes too long, stop loading
    const safetyTimer = setTimeout(() => {
      if (!profile) setLoading(false);
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, []);

  async function fetchProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) throw error;
      setProfile(data);

      if (data?.role === 'family') {
        supabase.from('storyteller_profiles').select('id')
          .eq('created_by', userId).limit(1).maybeSingle()
          .then(({ data: sp }) => setIsCaptain(!!sp));
      } else {
        setIsCaptain(false);
      }
    } catch (err) {
      console.error('fetchProfile error:', err.message);
    } finally {
      setLoading(false);
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setIsCaptain(false);
  }

  const value = {
    user,
    profile,
    loading,
    isCaptain,
    signOut,
    isStoryteller: profile?.role === 'storyteller',
    isFamily:      profile?.role === 'family',
    tier:          profile?.tier || 'story',
    refetchProfile: () => user && fetchProfile(user.id),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
